import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Radio, Select, Space, Table, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { createAssessmentInSessionInputSchema, recordExternalAssessmentInputSchema } from '@cc/schema';
import { SeriesChart } from '../../components/charts/SeriesChart';
import { AssignHomeworkDrawer } from '../homework/AssignHomeworkDrawer';
import {
  INSTRUMENT_IDS,
  TREND_FIELDS,
  TYPE_OF,
  allAnswered,
  blankAnswers,
  choicesOf,
  itemKey,
  ruleOfRow,
  scoreOf,
  scoreText,
  seriesOf,
  signed,
  type Change,
  type Definition,
  type Trend,
  type TrendRow,
} from './model';
import { useAssessmentsCommand } from './useAssessmentsCommand';

const SHORT_NAME: Record<string, string> = { phq9: 'PHQ-9', gad7: 'GAD-7', dass21: 'DASS-21', pcl5: 'PCL-5', gds15: 'GDS-15', urica: 'URICA' };
type Tr = (k: string, o?: Record<string, unknown>) => string;
const uuid = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

/**
 * E35 · ClientAssessmentTrend + RecordExternalAssessment + AssignHomework (assessment) + CreateAssessment (in session) - "Client Hub -
 * Assessments": every take of the client (their own and the ones entered in session) and every external result, interpreted, in
 * time order, for one instrument or all; a chart of the scores; Record external result, Assign this instrument and Take in session
 * are its sections. A PHQ-9 take shows its item 9 and the client's literal answer as a plain row, always: no colour, no badge, no
 * alert - displayed, never evaluated. A new result shows here the moment the client submits it; there is no email and no push
 * (Q-e35g1-1 A). Visible through a relation with VIEW permission; recording and entering a take need a granted relation (the server).
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru'; clientTimeZone?: string | null };

export const ClientAssessmentsTab = ({ clientId, clientName, locale, clientTimeZone }: Props) => {
  const t = useTranslate() as unknown as Tr;
  const dateLocale = locale === 'ru' ? ru : enUS;
  const { run, isLoading } = useAssessmentsCommand();
  const [instrument, setInstrument] = useState<string>('all');

  const trend = useCustom<Trend>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientAssessmentTrend',
      fields: TREND_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        instrumentId: { name: 'instrumentId', type: 'String', value: instrument === 'all' ? null : instrument },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const view = trend.data?.data;
  const rows = useMemo(() => [...(view?.rows ?? [])].reverse(), [view]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const day = (iso: string) => format(parseISO(iso), 'd MMM yyyy', { locale: dateLocale });
  const nameOf = (id: string) => t(`homework.target.assessment.${id}`);

  const changeText = (c: Change | null, rule: string | null) =>
    c
      ? [
          rule === 'uricaReadiness' ? `${c.total > 0 ? '+' : ''}${scoreText(rule, c.total)}` : signed(c.total),
          ...c.subscales.filter((s) => s.change !== 0).map((s) => `${s.change > 0 ? '+' : ''}${s.change}`),
        ]
          .slice(0, 1)
          .join(' ')
      : '';

  // ── record an external result ───────────────────────────────────────────────────────────────
  const [externalOpen, setExternalOpen] = useState(false);
  const [exName, setExName] = useState('');
  const [exScore, setExScore] = useState<number | null>(null);
  const [exMax, setExMax] = useState<number | null>(null);
  const [exDate, setExDate] = useState<Dayjs | null>(null);
  const [exNote, setExNote] = useState('');
  const [exError, setExError] = useState<string | null>(null);
  const openExternal = () => {
    setExName('');
    setExScore(null);
    setExMax(null);
    setExDate(dayjs());
    setExNote('');
    setExError(null);
    setMessage(null);
    setExternalOpen(true);
  };
  const saveExternal = async () => {
    const input = {
      externalAssessmentId: uuid(),
      subject: clientId,
      instrumentName: exName.trim(),
      score: exScore,
      maxScore: exMax,
      administeredAt: exDate ? exDate.toISOString() : null,
      note: exNote.trim() || null,
    };
    try {
      await recordExternalAssessmentInputSchema.validate(input);
    } catch {
      setExError(t('assessments.hub.external.invalid'));
      return;
    }
    const res = await run('recordExternalAssessment', input, t('assessments.hub.external.error'));
    if (res.ok) {
      setExternalOpen(false);
      setMessage({ type: 'success', text: t('assessments.hub.external.saved') });
      trend.refetch();
    } else setExError(res.message);
  };

  // ── assign this instrument ──────────────────────────────────────────────────────────────────
  const [assigning, setAssigning] = useState(false);

  // ── take in session ─────────────────────────────────────────────────────────────────────────
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionInstrument, setSessionInstrument] = useState<string>('phq9');
  const [sessionAnswers, setSessionAnswers] = useState<Array<number | null>>([]);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const sessionTrend = useCustom<Trend>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientAssessmentTrend',
      fields: TREND_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        instrumentId: { name: 'instrumentId', type: 'String', value: sessionInstrument },
      },
    },
    queryOptions: { enabled: sessionOpen, retry: false },
  });
  const sessionDef: Definition | null =
    sessionTrend.data?.data?.definition?.instrumentId === sessionInstrument ? sessionTrend.data.data.definition : null;
  const openSession = () => {
    setMessage(null);
    setSessionError(null);
    setSessionAnswers([]);
    setSessionInstrument(instrument === 'all' ? 'phq9' : instrument);
    setSessionOpen(true);
  };
  const answers = sessionDef && sessionAnswers.length === sessionDef.itemsCount ? sessionAnswers : sessionDef ? blankAnswers(sessionDef) : [];
  const saveSession = async () => {
    if (!sessionDef) return;
    const input = {
      assessmentId: uuid(),
      subject: clientId,
      type: TYPE_OF[sessionInstrument as keyof typeof TYPE_OF],
      answers,
      score: scoreOf(sessionDef, answers),
    };
    try {
      await createAssessmentInSessionInputSchema.validate(input);
    } catch {
      setSessionError(t('assessments.hub.session.invalid'));
      return;
    }
    const res = await run('createAssessmentInSession', input, t('assessments.hub.session.error'));
    if (res.ok) {
      setSessionOpen(false);
      setMessage({ type: 'success', text: t('assessments.hub.session.saved') });
      trend.refetch();
    } else setSessionError(res.message);
  };

  const refusal = trend.isError;

  const columns = [
    { title: t('assessments.hub.col.date'), key: 'date', render: (_: unknown, r: TrendRow) => day(r.occurredAt) },
    {
      title: t('assessments.hub.col.instrument'),
      key: 'instrument',
      render: (_: unknown, r: TrendRow) => (r.instrumentId ? nameOf(r.instrumentId) : r.instrumentName),
    },
    {
      title: t('assessments.hub.col.source'),
      key: 'source',
      render: (_: unknown, r: TrendRow) => (
        <Tag data-testid={`assessments-source-${r.source}`}>{t(`assessments.hub.source.${r.source}`)}</Tag>
      ),
    },
    {
      title: t('assessments.hub.col.score'),
      key: 'score',
      render: (_: unknown, r: TrendRow) => (
        <span data-testid="assessments-total">
          {r.maxScore ? `${r.total} / ${r.maxScore}` : scoreText(ruleOfRow(r, view?.definition), r.total)}
        </span>
      ),
    },
    {
      title: t('assessments.hub.col.bands'),
      key: 'bands',
      render: (_: unknown, r: TrendRow) => {
        // an instrument with a band on its total (PCL-5, GDS-15) names it first; a scale instrument (DASS-21) has none - its scales carry theirs
        const total = r.bandKey && !r.bandIsWorstSubscale ? t(r.bandKey) : '';
        const scales = r.subscales
          .map(
            (s) =>
              `${t(`assessments.subscale.${r.instrumentId}.${s.name}`)} ${scoreText(ruleOfRow(r, view?.definition), s.score)}${s.bandKey ? ` · ${t(s.bandKey)}` : ''}`,
          )
          .join('; ');
        return [total, scales].filter(Boolean).join(' — ');
      },
    },
    {
      title: t('assessments.hub.col.change'),
      key: 'change',
      render: (_: unknown, r: TrendRow) => changeText(r.changeSinceLast, ruleOfRow(r, view?.definition)),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="client-assessments-tab">
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Select
          data-testid="assessments-instrument"
          style={{ minWidth: 220 }}
          value={instrument}
          onChange={setInstrument}
          options={[
            { value: 'all', label: t('assessments.hub.all') },
            ...INSTRUMENT_IDS.map((id) => ({ value: id, label: nameOf(id) })),
          ]}
        />
        <Space wrap>
          <Button data-testid="assessments-record-external" onClick={openExternal}>
            {t('assessments.hub.external.button')}
          </Button>
          <Button
            data-testid="assessments-assign"
            disabled={instrument === 'all'}
            onClick={() => setAssigning(true)}>
            {t('assessments.hub.assign.button')}
          </Button>
          <Button type="primary" data-testid="assessments-take-in-session" onClick={openSession}>
            {t('assessments.hub.session.button')}
          </Button>
        </Space>
      </Space>
      {message ? <Alert showIcon type={message.type} message={message.text} data-testid="assessments-message" /> : null}
      {refusal ? (
        <Typography.Paragraph type="secondary" data-testid="assessments-not-shared">
          {t('pages.clientsShow.notShared')}
        </Typography.Paragraph>
      ) : null}
      {!refusal && rows.length === 0 && !trend.isLoading ? (
        <Typography.Paragraph type="secondary" data-testid="assessments-empty">
          {t('assessments.hub.empty', { name: clientName })}
        </Typography.Paragraph>
      ) : null}
      {instrument !== 'all' && (view?.rows.filter((r) => r.source !== 'external').length ?? 0) > 1 ? (
        <SeriesChart
          series={seriesOf(view!.rows, (k) => t(`assessments.subscale.${instrument}.${k}`), t('assessments.result.total'))}
          locale={locale}
          ariaLabel={t('assessments.hub.chart')}
          testId="assessments-chart"
        />
      ) : null}
      {instrument === 'pcl5' ? (
        <Typography.Paragraph type="secondary">{t('assessments.hub.pcl5Change')}</Typography.Paragraph>
      ) : null}
      {rows.length > 0 ? (
        <Table<TrendRow>
          size="small"
          pagination={false}
          rowKey={(r) => r.assessmentId ?? r.externalAssessmentId ?? r.occurredAt}
          columns={columns}
          dataSource={rows}
          onRow={(r) => ({ 'data-testid': `assessments-row-${r.source}` }) as never}
          expandable={{
            // item 9 and a note are ALWAYS shown (displayed, never evaluated): the rows that carry one are expanded, controlled
            expandedRowKeys: rows
              .filter((r) => Boolean(r.item9 || r.note))
              .map((r) => r.assessmentId ?? r.externalAssessmentId ?? r.occurredAt),
            showExpandColumn: false,
            rowExpandable: (r) => Boolean(r.item9 || r.note),
            expandedRowRender: (r) => (
              <div>
                {r.item9 ? (
                  <div data-testid="assessments-item9">
                    {t('assessments.hub.item9', {
                      question: t(r.item9.questionKey),
                      answer: r.item9.answerKey ? t(r.item9.answerKey) : t('assessments.hub.noAnswer'),
                    })}
                  </div>
                ) : null}
                {r.note ? <div data-testid="assessments-note">{r.note}</div> : null}
              </div>
            ),
          }}
        />
      ) : null}

      <Modal
        open={externalOpen}
        title={t('assessments.hub.external.title')}
        onCancel={() => setExternalOpen(false)}
        onOk={saveExternal}
        okText={t('assessments.hub.external.save')}
        cancelText={t('common.cancel')}
        confirmLoading={isLoading}
        okButtonProps={{ 'data-testid': 'assessments-external-save' } as never}
        destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">{t('assessments.hub.external.body')}</Typography.Text>
          <Input
            data-testid="assessments-external-name"
            placeholder={t('assessments.hub.external.name')}
            value={exName}
            onChange={(e) => setExName(e.target.value)}
          />
          <Space>
            <InputNumber
              data-testid="assessments-external-score"
              placeholder={t('assessments.hub.external.score')}
              min={0}
              value={exScore}
              onChange={(v) => setExScore(v)}
            />
            <InputNumber
              data-testid="assessments-external-max"
              placeholder={t('assessments.hub.external.max')}
              min={1}
              value={exMax}
              onChange={(v) => setExMax(v)}
            />
          </Space>
          <DatePicker data-testid="assessments-external-date" value={exDate} onChange={setExDate} />
          <Input.TextArea
            data-testid="assessments-external-note"
            placeholder={t('assessments.hub.external.note')}
            value={exNote}
            onChange={(e) => setExNote(e.target.value)}
          />
          {exError ? <Alert type="error" showIcon message={exError} data-testid="assessments-external-error" /> : null}
        </Space>
      </Modal>

      <Modal
        open={sessionOpen}
        title={t('assessments.hub.session.title')}
        width={720}
        onCancel={() => setSessionOpen(false)}
        onOk={saveSession}
        okText={t('assessments.hub.session.save')}
        cancelText={t('common.cancel')}
        confirmLoading={isLoading}
        okButtonProps={{ disabled: !sessionDef || !allAnswered(answers), 'data-testid': 'assessments-session-save' } as never}
        destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">{t('assessments.hub.session.body')}</Typography.Text>
          <Select
            data-testid="assessments-session-instrument"
            style={{ width: '100%' }}
            value={sessionInstrument}
            onChange={(v) => {
              setSessionInstrument(v);
              setSessionAnswers([]);
            }}
            options={INSTRUMENT_IDS.map((id) => ({ value: id, label: nameOf(id) }))}
          />
          {sessionDef ? (
            <>
              <Typography.Paragraph type="secondary">{t(`assessments.${sessionDef.instrumentId}.instruction`, { defaultValue: '' })}</Typography.Paragraph>
              <Typography.Text strong>{t(`assessments.${sessionDef.instrumentId}.stem`, { defaultValue: '' })}</Typography.Text>
              <div style={{ maxHeight: 360, overflowY: 'auto' }} data-testid="assessments-session-items">
                {Array.from({ length: sessionDef.itemsCount }, (_, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div>
                      {i + 1}. {t(itemKey(sessionDef, i))}
                    </div>
                    <Radio.Group
                      data-testid={`assessments-session-item-${i + 1}`}
                      value={answers[i]}
                      onChange={(e) => {
                        const next = [...answers];
                        next[i] = e.target.value;
                        setSessionAnswers(next);
                      }}>
                      {choicesOf(sessionDef).map((c) => (
                        <Radio key={c.value} value={c.value} data-testid={`assessments-session-item-${i + 1}-${c.value}`}>
                          {t(c.labelKey)}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </div>
                ))}
              </div>
              <Typography.Text data-testid="assessments-session-score">
                {t('assessments.result.total')}: {scoreText(sessionDef.scoringRule, scoreOf(sessionDef, answers))}
              </Typography.Text>
            </>
          ) : null}
          {sessionError ? <Alert type="error" showIcon message={sessionError} data-testid="assessments-session-error" /> : null}
        </Space>
      </Modal>

      <AssignHomeworkDrawer
        open={assigning}
        preset={{
          kind: 'assessment',
          target: instrument === 'all' ? undefined : instrument,
          // the title starts as the questionnaire's name (still editable), like a worksheet's
          title: instrument === 'all' ? undefined : (SHORT_NAME[instrument] ?? instrument),
        }}
        clients={[{ subject: clientId, name: clientName }]}
        clientTimeZone={clientTimeZone}
        onClose={() => setAssigning(false)}
        onAssigned={() => trend.refetch()}
      />
    </Space>
  );
};
